/**
 * pickTab — choose the FILLED "Assignment Sheet" tab from a multi-tab Prelim Anes
 * Assignments workbook.
 *
 * The daily file has several tabs (Qgenda Report, Assignment Sheet, Sign off,
 * Assignment Backup, Template …). The first tab is usually a blank template, so
 * reading a bare "A:Z" range (which the Sheets API resolves to the FIRST tab)
 * returns rooms with no case data and the importer finds "no Wesley Chapel rooms".
 * These pure helpers pick the tab that actually parses to WC rows, preferring the
 * live "Assignment Sheet" over backups/templates.
 *
 * Framework-free, no network — shared by the browser importer (sheetsClient) and
 * the scheduled Cloud Function (dailyImport), so both target the same tab.
 */
import { parsePrelimAssignments } from "./parsePrelimAssignments";

const DEFAULT_RANGE = "A:Z";

/** Quote a tab title for an A1 range: wrap in single quotes, escape inner quotes. */
export const tabRange = (title, range = DEFAULT_RANGE) =>
  `'${String(title).replace(/'/g, "''")}'!${range}`;

/**
 * Score a tab as a candidate for "today's filled Wesley Chapel assignments".
 * Primary signal = how many WC rows the parser keeps (rows with a time or CRNA);
 * the blank template tab scores 0 and is never chosen. Name is a tie-breaker: the
 * live "Assignment Sheet" wins over backups/templates/other views that may also
 * carry WC rows.
 */
export function scoreTabForAssignments(title, wcRowCount) {
  if (!wcRowCount) return -1; // no usable WC data → never pick
  let score = wcRowCount;
  const t = String(title || "").toLowerCase();
  if (/assignment/.test(t) && !/(backup|template|master|archive)/.test(t)) score += 100000;
  if (/(backup|template|master|archive|qgenda|sign\s*off|room\s*def|import\s*data)/.test(t)) score -= 50000;
  return score;
}

/**
 * Pick the best tab from [{title, values}] using scoreTabForAssignments. Returns
 * { values, title } for the winner, or null when no tab has any WC rows.
 */
export function pickAssignmentsTab(tabs = []) {
  let best = null;
  for (const tab of tabs) {
    const { rows } = parsePrelimAssignments(tab.values || []);
    const score = scoreTabForAssignments(tab.title, rows.length);
    if (!best || score > best.score) best = { values: tab.values || [], title: tab.title, score };
  }
  if (!best || best.score < 0) return null;
  return { values: best.values, title: best.title };
}
